import React from 'react';
import { Tooltip } from 'antd';
import cn from 'classnames';
import IndicatorBadge from '../../../../common/IndicatorBadge/IndicatorBadge';
import { convertPenniesToRub } from '../../../../../../../helpers/convertPenniesToRub';
import {
  requestInFlatStagesColorsMap,
  requestInPeriodColorMap,
} from '../../../../../constants/stagesColorMaps';
import { requestInFlatStatuses } from '../../../../../constants/common';
import { texts } from '../../../../../constants/texts';
import styles from './FlatCell.module.scss';
import { useSelector } from 'react-redux';
import { chessViewMode } from '../../../../../constants/chessViewMode';
import useGetIndicatorsFlat from '../../../../../hooks/useGetIndicatorsFlat';
import { requestFlatPeriod } from '../../../../../constants/requestFlat';
import { generateExpWithNum } from '../../../../../utils/generateExpWithNum';
import { declensions } from '../../../../../constants/declensions';

const badgeColors = {
  immediate: requestInFlatStagesColorsMap.get(requestInFlatStatuses.immediate),
  debt: requestInFlatStagesColorsMap.get(requestInFlatStatuses.debt),
  active: requestInFlatStagesColorsMap.get(requestInFlatStatuses.active),
  comment: requestInFlatStagesColorsMap.get(requestInFlatStatuses.comment),
};

const badgeColorsRequest = {
  rightTime: requestInPeriodColorMap.get(requestFlatPeriod.rightTime),
  expired: requestInPeriodColorMap.get(requestFlatPeriod.expired),
  excess: requestInPeriodColorMap.get(requestFlatPeriod.excess),
  finishingTouches: requestInPeriodColorMap.get(
    requestFlatPeriod.finishingTouches
  ),
};

const DEFAULT_BG_COLOR = '#efefef';
const HOVER_BG_COLOR = '#5361f3';
const DEFAULT_BORDER_COLOR = 'transparent';

const getRootElement = (element) => {
  if (element.dataset.floor) {
    return element;
  }

  return getRootElement(element.parentNode);
};

const FlatCell = ({
  flatData,
  entranceName,
  onClick,
  isPreview,
  isOffice = false,
  bgColor = DEFAULT_BG_COLOR,
  borderColor = DEFAULT_BORDER_COLOR,
  ...props
}) => {
  const {
    existDebt,
    immediateRequestCount,
    activeRequestCount,
    amount,
    hasComment,
    floor,
    address,
  } = flatData;

  const { sumDeadline, sumExpired, sumExcess } = useGetIndicatorsFlat(
    address?.id
  );

  const { chessViewColorMode, valuesRequestAttributes } = useSelector(
    (state) => state.housingStockStore
  );

  const hasVisibleAddressTypeFinishId =
    (address?.addressTypeFinishId === 4 ||
      address?.addressTypeFinishParentId === 4) &&
    (!!valuesRequestAttributes?.length
      ? valuesRequestAttributes.includes(requestFlatPeriod.finishingTouches)
      : true);
  const hasVisibleSumDeadlineBadge =
    !!sumDeadline &&
    (!!valuesRequestAttributes?.length
      ? valuesRequestAttributes.includes(requestFlatPeriod.rightTime)
      : true);
  const hasVisibleSumExcessBadge =
    !!sumExcess &&
    (!!valuesRequestAttributes?.length
      ? valuesRequestAttributes.includes(requestFlatPeriod.excess)
      : true);
  const hasVisibleSumExpiredBadge =
    !!sumExpired &&
    (!!valuesRequestAttributes?.length
      ? valuesRequestAttributes.includes(requestFlatPeriod.expired)
      : true);

  const handleClick = () => {
    onClick(flatData);
  };

  const handleMouseEnter = (e) => {
    const element = getRootElement(e.currentTarget);
    element.style.backgroundColor = HOVER_BG_COLOR;
  };

  const handleMouseLeave = (e) => {
    const element = getRootElement(e.currentTarget);
    element.style.backgroundColor = bgColor;
  };

  const hasIndicator = Boolean(
    (existDebt || immediateRequestCount || activeRequestCount || hasComment) &&
      chessViewColorMode === chessViewMode.stages
  );

  const hasIndicatorsRequest = Boolean(
    chessViewColorMode === chessViewMode.applicationMonitoring
  );

  return (
    <Tooltip placement="bottom" title={address?.synonym || ''}>
      <div
        {...props}
        style={{
          ...props.style,
          backgroundColor: bgColor,
          borderColor: borderColor,
        }}
        className={cn(styles.root, {
          [styles.notPreview]: !isPreview,
          [styles.additionalInfo]: address?.synonym,
        })}
        onClick={onClick ? handleClick : undefined}
        onMouseEnter={!isPreview ? handleMouseEnter : undefined}
        onMouseLeave={!isPreview ? handleMouseLeave : undefined}
        data-floor={`${floor} этаж`}
      >
        {address?.name || ''}
        {hasIndicator && (
          <div className={styles.indicatorsContainer}>
            {existDebt && (
              <Tooltip
                title={
                  amount
                    ? `${texts.debt}: ${convertPenniesToRub(-1 * amount)} руб.`
                    : ''
                }
              >
                <IndicatorBadge withBorder bgColor={badgeColors.debt} />
              </Tooltip>
            )}
            {Boolean(immediateRequestCount) && (
              <IndicatorBadge withBorder bgColor={badgeColors.immediate} />
            )}
            {Boolean(activeRequestCount) && (
              <IndicatorBadge withBorder bgColor={badgeColors.active} />
            )}
            {hasComment && (
              <IndicatorBadge withBorder bgColor={badgeColors.comment} />
            )}
          </div>
        )}
        {hasIndicatorsRequest && (
          <div className={styles.indicatorsContainer}>
            {!!hasVisibleSumDeadlineBadge && (
              <Tooltip
                title={generateExpWithNum(sumDeadline, declensions.request)}
              >
                <IndicatorBadge
                  withBorder
                  bgColor={badgeColorsRequest.rightTime}
                />
              </Tooltip>
            )}
            {!!hasVisibleSumExpiredBadge && (
              <Tooltip
                title={generateExpWithNum(sumExpired, declensions.request)}
              >
                <IndicatorBadge
                  withBorder
                  bgColor={badgeColorsRequest.expired}
                />
              </Tooltip>
            )}
            {!!hasVisibleSumExcessBadge && (
              <Tooltip
                title={generateExpWithNum(sumExcess, declensions.request)}
              >
                <IndicatorBadge
                  withBorder
                  bgColor={badgeColorsRequest.excess}
                />
              </Tooltip>
            )}
            {!!hasVisibleAddressTypeFinishId && (
              <Tooltip title="Чистовая отделка">
                <IndicatorBadge
                  withBorder
                  bgColor={badgeColorsRequest.finishingTouches}
                />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default FlatCell;
